import apiClient from "../config/axiosConfig.js";

/**
 * Projects — named sets of files that (from Phase 2) also carry the folder
 * ruleset the desktop app uses to lay them out on disk.
 *
 * Membership lives on the file as `metadata.projectIds` and is written ONLY
 * through the two member routes below. The backend deliberately ignores a
 * `projectIds` sent through the generic metadata-update endpoint, because that
 * path can't check the project exists in this workspace.
 */

export async function listProjects() {
    const res = await apiClient.get('/api/projects');
    return res.data?.projects ?? [];
}

export async function getProject(id, options = {}) {
    const res = await apiClient.get('/api/projects/one', { params: { id }, ...options });
    return res.data?.project;
}

/** File count + total bytes. Counts the whole membership, so it isn't free. */
export async function getProjectStats(id, options = {}) {
    const res = await apiClient.get('/api/projects/stats', { params: { id }, ...options });
    return { fileCount: res.data?.fileCount ?? 0, totalBytes: res.data?.totalBytes ?? 0 };
}

/**
 * A project's files AND its totals from one membership scan — the detail view
 * needs both, and this avoids scanning twice.
 *
 * The list is capped (`truncated` says so) while the counts always cover the
 * whole membership. Anything that needs the full set at scale should use the
 * sync manifest, not this.
 */
export async function listProjectFiles(id, { limit } = {}, options = {}) {
    const res = await apiClient.get('/api/projects/files', { params: { id, limit }, ...options });
    return {
        files: res.data?.files ?? [],
        fileCount: res.data?.fileCount ?? 0,
        totalBytes: res.data?.totalBytes ?? 0,
        truncated: res.data?.truncated ?? false,
    };
}

export async function createProject({ name, description, color }, options = {}) {
    const res = await apiClient.post('/api/projects', { name, description, color }, options);
    return res.data?.project;
}

/** `patch` may contain any of { name, description, color, status, collisionPolicy }. */
export async function updateProject(id, patch, options = {}) {
    const res = await apiClient.patch('/api/projects', { id, ...patch }, options);
    return res.data?.project;
}

/**
 * Delete a project. Files are NEVER deleted — they're detached from it — and
 * the response says how many were touched so the UI can report it.
 */
export async function deleteProject(id, options = {}) {
    const res = await apiClient.delete('/api/projects', { params: { id }, ...options });
    return { detachedFiles: res.data?.detachedFiles ?? 0 };
}

/**
 * Add files to a project in ONE request (up to 5000).
 *
 * Returns `{ changed, skipped }`; ids that are missing or belong to another
 * workspace come back in `skipped` rather than failing the whole call, so a
 * bulk action over a stale selection still does the right thing for the rest.
 */
export async function addFilesToProject(projectId, fileIds, options = {}) {
    const res = await apiClient.post('/api/projects/members/add', { projectId, fileIds }, options);
    return { changed: res.data?.changed ?? 0, skipped: res.data?.skipped ?? [] };
}

export async function removeFilesFromProject(projectId, fileIds, options = {}) {
    const res = await apiClient.post('/api/projects/members/remove', { projectId, fileIds }, options);
    return { changed: res.data?.changed ?? 0, skipped: res.data?.skipped ?? [] };
}

/** The project's currently-saved layout, resolved against its real files. */
export async function getProjectLayout(id, options = {}) {
    const res = await apiClient.get('/api/projects/layout', { params: { id }, ...options });
    return {
        entries: res.data?.entries ?? [],
        collisions: res.data?.collisions ?? [],
        unmatchedCount: res.data?.unmatchedCount ?? 0,
        layoutRev: res.data?.layoutRev ?? 0,
    };
}

/**
 * Resolve an UNSAVED ruleset against the project's real files.
 *
 * Runs the same server code path a save would, so the preview and the save can't
 * disagree. An invalid ruleset comes back `valid: false` with per-rule issues
 * rather than an HTTP error, so the editor can annotate rules while you type.
 */
/**
 * Read a Bubbly Asset Rules bundle and turn its folder structure into QDex
 * layout rules.
 *
 * `subPath` is what lets a QDex project sit at any level of the rules tree — a
 * project bound to `Assets/Exported` wants templates relative to THAT, not to
 * the workspace root. Pass `bundle` instead of `url` to skip the fetch entirely
 * (and with it the host allowlist).
 *
 * @returns {Promise<{version: string|null, sources: Array<object>, treeId: string,
 *                    subPath: string, subPathOptions: Array<object>,
 *                    rules: Array<object>, fallbackPath: string,
 *                    warnings: string[], folders: Array<object>}>}
 */
export async function previewAssetRules({ url, bundle, treeId, subPath } = {}, options = {}) {
    const res = await apiClient.post(
        '/api/projects/asset-rules/preview',
        { url, bundle, treeId, subPath },
        options,
    );
    return res.data;
}

export async function previewProjectLayout(projectId, { layoutRules, fallbackPath, extGroups }, options = {}) {
    const res = await apiClient.post(
        '/api/projects/layout/preview',
        { projectId, layoutRules, fallbackPath, extGroups },
        options,
    );
    return {
        valid: res.data?.valid ?? false,
        issues: res.data?.issues ?? [],
        entries: res.data?.entries ?? [],
        collisions: res.data?.collisions ?? [],
        unmatchedCount: res.data?.unmatchedCount ?? 0,
        movedCount: res.data?.movedCount ?? 0,
    };
}

/**
 * The project's metadata checklist — collisions, files no rule matched,
 * untagged files, missing required companions, duplicates.
 *
 * Computed server-side from the materialized layout index, so this is one small
 * request rather than the browser re-resolving a whole project. The score is
 * only a nudge to read the list; `checks` is the substance and each row carries
 * its own count, samples and one-line fix.
 */
export async function getProjectHealth(id, options = {}) {
    const res = await apiClient.get('/api/projects/health', { params: { id }, ...options });
    return {
        score: res.data?.score ?? 100,
        fileCount: res.data?.fileCount ?? 0,
        ruleCount: res.data?.ruleCount ?? 0,
        checkedAt: res.data?.checkedAt ?? null,
        checks: res.data?.checks ?? [],
        passed: res.data?.passed ?? [],
    };
}
